// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — components/NaturezaOperacaoSearchCombobox.tsx
//
//  Escolhe uma natureza de operação do cadastro (GET /api/naturezas-operacao),
//  com busca por descrição, no lugar de texto livre ou Select sem busca.
//  A lista é curta (dezenas): busca em memória sobre o combo em cache.
// ════════════════════════════════════════════════════════════════════════════

import { EntityCombobox } from "@/components/EntityCombobox";
import { naturezaOperacaoService } from "@/features/fiscal/services/fiscalService";
import type { NaturezaOperacaoComboDto } from "@/features/fiscal/types";

let cache: { em: number; itens: Promise<NaturezaOperacaoComboDto[]> } | null = null;
const carregar = () => {
  if (!cache || Date.now() - cache.em > 5 * 60_000)
    cache = { em: Date.now(), itens: naturezaOperacaoService.combo().catch((e) => { cache = null; throw e; }) };
  return cache.itens;
};

const normalizar = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

async function buscar(termo: string): Promise<NaturezaOperacaoComboDto[]> {
  const todas = await carregar();
  const t = normalizar(termo.trim());
  return (t ? todas.filter((n) => normalizar(n.descricao ?? "").includes(t)) : todas).slice(0, 50);
}

async function obterPorId(id: number): Promise<NaturezaOperacaoComboDto> {
  const n = (await carregar()).find((x) => x.id === id);
  if (!n) throw new Error("Natureza de operação não encontrada.");
  return n;
}

interface Props {
  value?: number | null;
  onChange?: (id: number | null) => void;
  onSelect?: (natureza: NaturezaOperacaoComboDto | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function NaturezaOperacaoSearchCombobox({ placeholder = "Selecione a natureza da operação...", ...props }: Props) {
  return (
    <EntityCombobox<NaturezaOperacaoComboDto>
      {...props}
      queryKey="natureza-operacao-lookup"
      buscar={buscar}
      obterPorId={obterPorId}
      getId={(n) => n.id}
      getDescricao={(n) => n.descricao || "(sem descrição)"}
      minCaracteres={0}
      placeholder={placeholder}
      placeholderBusca="Buscar natureza..."
    />
  );
}
